/**
 * Homepage "Across the markets" rows: the three governed Terminal measures.
 * Port of canideal.org assets/homepage/market-data.js (with the year-agnostic
 * period fix from lib/periods.js): only rows from the source-free Terminal
 * projection (GET /api/market/public/terminal) that match a spec exactly, carry
 * a fresh proof clock and a complete reporting period are shown. Withdrawals,
 * stale proofs and out-of-order snapshots never resurrect or replace newer data.
 */
import { formatExact } from '@/lib/format';
import { parsePeriod } from '@/lib/periods';
import type { FeedRow } from '@/lib/types';

/** A proof older than this is expired (48 h). */
export const MARKET_ROW_MAX_AGE = 48 * 60 * 60 * 1000;
/** Poll cadence (5 min) and per-request deadline. */
export const MARKET_ROW_INTERVAL = 5 * 60 * 1000;
export const MARKET_ROW_TIMEOUT = 8000;
/** While a partial snapshot warms up, re-check after 15 s (at most twice). */
export const MARKET_ROW_WARMUP_DELAY = 15000;
export const MARKET_ROW_WARMUP_RETRIES = 2;

export type MarketRowKey = 'de' | 'ca' | 'uy';

export interface MarketRowSpec {
  key: MarketRowKey;
  id: string;
  country: string;
  metric: string;
  kind: string;
  product: string;
  market: string;
  unit: string;
  periodType: 'quarter' | 'month';
}

export const MARKET_ROW_SPECS: readonly MarketRowSpec[] = Object.freeze([
  {
    key: 'de',
    id: 'signal-de-imports_kg',
    country: 'Germany',
    metric: 'imports_kg',
    kind: 'border_volume',
    product: 'flower',
    market: 'medical',
    unit: 'kg',
    periodType: 'quarter',
  },
  {
    key: 'ca',
    id: 'signal-ca-medical_flower_units',
    country: 'Canada',
    metric: 'medical_flower_units',
    kind: 'sales_volume',
    product: 'flower',
    market: 'medical',
    unit: 'packaged units',
    periodType: 'month',
  },
  {
    key: 'uy',
    id: 'signal-uy-pharmacy_sales',
    country: 'Uruguay',
    metric: 'pharmacy_sales',
    kind: 'sales_volume',
    product: 'flower',
    market: 'adult_use',
    unit: 'grams',
    periodType: 'month',
  },
]);

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export interface MarketRowRecord {
  value: number;
  period: string;
  rank: number;
  label: string;
  proof: number;
}

export type MarketRowReason =
  | 'pending'
  | 'available'
  | 'expired'
  | 'unavailable'
  | 'withdrawn'
  | 'delayed';

export interface MarketRowEntry {
  record: MarketRowRecord | null;
  /** Newest record ever shown; later snapshots must not go backwards from it. */
  watermark: MarketRowRecord | null;
  reason: MarketRowReason;
  blocked: boolean;
  floor?: number;
}

export type MarketRowsState = Record<MarketRowKey, MarketRowEntry>;

/** Strict UTC timestamp ('YYYY-MM-DDTHH:MM:SS[.fff]Z') → ms, else NaN. */
export function proofTimestamp(value: unknown): number {
  if (
    typeof value !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(value)
  ) {
    return NaN;
  }
  const result = Date.parse(value);
  return Number.isFinite(result) &&
    new Date(result).toISOString().slice(0, 19) === value.slice(0, 19)
    ? result
    : NaN;
}

const fresh = (proof: number, now: number) =>
  Number.isFinite(proof) &&
  Number.isFinite(now) &&
  proof <= now &&
  now - proof <= MARKET_ROW_MAX_AGE;

/**
 * A complete reporting period (ended by `now`) in reporting scope. The rank is
 * monotonic across years so 2027 Q1 supersedes 2026 Q4.
 */
export function reportingPeriod(value: unknown, type: MarketRowSpec['periodType'], now: number) {
  const period = parsePeriod(value, { frequencies: [type], now });
  if (!period || period.next > now) return null;
  return {
    value: period.value,
    rank: period.index,
    label:
      type === 'quarter'
        ? `${period.year} Q${period.number}`
        : `${MONTHS[period.number - 1]} ${period.year}`,
  };
}

const entry = (): MarketRowEntry => ({
  record: null,
  watermark: null,
  reason: 'pending',
  blocked: false,
});

export function emptyMarketRows(): MarketRowsState {
  return { de: entry(), ca: entry(), uy: entry() };
}

/** Drops displayed records whose proof has aged out. */
export function expireMarketRows(state: MarketRowsState | null, now: number): MarketRowsState {
  const next = emptyMarketRows();
  for (const spec of MARKET_ROW_SPECS) {
    const previous = state?.[spec.key] ?? entry();
    next[spec.key] =
      previous.record && !fresh(previous.record.proof, now)
        ? { ...previous, record: null, reason: 'expired' }
        : { ...previous };
  }
  return next;
}

function withdrawn(row: FeedRow | null | undefined): boolean {
  return (
    row?.withdrawn === true ||
    row?.suppressed === true ||
    ['withdrawn', 'suppressed', 'quarantined', 'held'].includes(String(row?.state)) ||
    ['withdrawn', 'suppressed', 'quarantined'].includes(String(row?.status))
  );
}

const SPEC_FIELDS = ['id', 'country', 'metric', 'kind', 'product', 'market', 'unit'] as const;

function candidate(
  row: FeedRow,
  spec: MarketRowSpec,
  now: number,
  checked: number
): MarketRowRecord | null {
  if (
    !row ||
    typeof row !== 'object' ||
    withdrawn(row) ||
    !SPEC_FIELDS.every((key) => row[key] === spec[key]) ||
    typeof row.value !== 'number' ||
    !Number.isFinite(row.value) ||
    row.value < 0
  ) {
    return null;
  }
  const proof = proofTimestamp(row.checked_at);
  const period = reportingPeriod(row.period, spec.periodType, now);
  if (!fresh(proof, now) || proof > checked || !period) return null;
  // Project an allowlist only; contributor identities and feed prose stay private.
  return { value: row.value, period: period.value, rank: period.rank, label: period.label, proof };
}

function clearRecord(
  previous: MarketRowEntry,
  reason: MarketRowReason,
  checked: number
): MarketRowEntry {
  // Once displayed evidence is explicitly withdrawn, a later-arriving cached
  // proof from before that withdrawal cannot restore it.
  const floor = previous.watermark
    ? Math.max(
        previous.floor || 0,
        previous.watermark.proof,
        Number.isFinite(checked) ? checked : 0
      )
    : 0;
  return { ...previous, record: null, reason, blocked: true, floor };
}

const IDENTITY = [
  'country',
  'metric',
  'kind',
  'product',
  'market',
  'unit',
  'period',
  'value',
] as const;

/** Applies one Terminal snapshot (or null for an invalid feed) to the row state. */
export function reconcileMarketRows(
  state: MarketRowsState | null,
  payload: unknown,
  now: number
): MarketRowsState {
  const next = expireMarketRows(state, now);
  const feed = payload as { status?: unknown; signals?: unknown; checked_at?: unknown } | null;
  const checked = proofTimestamp(feed?.checked_at);
  const shape =
    feed &&
    typeof feed === 'object' &&
    ['current', 'partial', 'unavailable'].includes(String(feed.status)) &&
    Array.isArray(feed.signals);
  if (!shape || feed.status === 'unavailable' || !fresh(checked, now)) {
    for (const spec of MARKET_ROW_SPECS) {
      next[spec.key] = clearRecord(
        next[spec.key],
        'unavailable',
        fresh(checked, now) ? checked : NaN
      );
    }
    return next;
  }
  const signals = feed.signals as FeedRow[];
  const suppressedIds = (payload as { suppressed_signal_ids?: unknown }).suppressed_signal_ids;
  const suppressed = new Set(Array.isArray(suppressedIds) ? suppressedIds : []);
  for (const spec of MARKET_ROW_SPECS) {
    const previous = next[spec.key];
    const rows = signals.filter((row) => row?.id === spec.id);
    if (suppressed.has(spec.id) || rows.some(withdrawn)) {
      next[spec.key] = clearRecord(previous, 'withdrawn', checked);
      continue;
    }
    // A successful governed snapshot omits withdrawn or ineligible rows. Unlike
    // a transport error, absence is authoritative and must not resurrect old data.
    if (!rows.length) {
      next[spec.key] = clearRecord(previous, 'unavailable', checked);
      continue;
    }
    const records = rows.map((row) => candidate(row, spec, now, checked));
    const identity = (row: FeedRow) => JSON.stringify(IDENTITY.map((key) => row?.[key]));
    if (records.some((row) => !row) || new Set(rows.map(identity)).size !== 1) {
      next[spec.key] = clearRecord(previous, 'unavailable', checked);
      continue;
    }
    const record = (records as MarketRowRecord[]).reduce((latest, row) =>
      row.proof > latest.proof ? row : latest
    );
    const mark = previous.watermark;
    if (
      mark &&
      (record.rank < mark.rank ||
        record.proof < mark.proof ||
        (record.rank > mark.rank && record.proof <= mark.proof) ||
        (previous.blocked && record.proof <= Math.max(mark.proof, previous.floor || 0)))
    ) {
      next[spec.key] = { ...previous, reason: previous.record ? 'delayed' : previous.reason };
      continue;
    }
    if (
      mark &&
      record.rank === mark.rank &&
      record.proof === mark.proof &&
      record.value !== mark.value
    ) {
      next[spec.key] = clearRecord(previous, 'unavailable', checked);
      continue;
    }
    // A changed value for the same period is accepted only with newer verified
    // proof; a repeat fetch time never substitutes for the row's proof clock.
    next[spec.key] = { record, watermark: record, reason: 'available', blocked: false };
  }
  return next;
}

export interface MarketRowDisplay {
  value: string;
  period: string;
}

/** Text for one row: the exact reported value and its period, or the awaiting state. */
export function marketRowDisplay(row: MarketRowEntry): MarketRowDisplay {
  return row.record
    ? { value: formatExact(row.record.value), period: row.record.label }
    : { value: '—', period: 'Awaiting verified data' };
}

/** Footer status line (honest about delays, partial coverage and warm-up). */
export function marketRowsStatus(
  state: MarketRowsState,
  {
    transportDelayed = false,
    warmingUp = false,
  }: { transportDelayed?: boolean; warmingUp?: boolean } = {}
): string {
  const available = MARKET_ROW_SPECS.filter((spec) => state[spec.key].record).length;
  const delayed =
    transportDelayed || MARKET_ROW_SPECS.some((spec) => state[spec.key].reason === 'delayed');
  if (delayed && available) {
    return 'Refresh delayed. Available figures retain their verified reporting periods; different measures are not directly comparable.';
  }
  if (available === MARKET_ROW_SPECS.length) {
    return 'Market figures show their reporting periods. Different measures are not directly comparable.';
  }
  if (warmingUp) {
    return 'Checking reported market figures. Available figures show their reporting periods; different measures are not directly comparable.';
  }
  return 'Some reported market figures are awaiting verification. Available figures show their reporting periods; different measures are not directly comparable.';
}

/** Earliest instant a displayed record expires (ms), or Infinity. */
export function nextMarketRowExpiry(state: MarketRowsState): number {
  const expiries = MARKET_ROW_SPECS.map((spec) => state[spec.key].record)
    .filter((row): row is MarketRowRecord => Boolean(row))
    .map((row) => row.proof + MARKET_ROW_MAX_AGE + 1);
  return Math.min(Infinity, ...expiries);
}
